import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import {
  Bars,
  Card,
  CodeBlock,
  CopyButton,
  ErrorNote,
  Eyebrow,
  Field,
  Kpi,
  PageHead,
  Spinner,
} from '../../components/ui'
import { api, wsPath } from '../../lib/api'
import { fmtDate, fmtMoney, fmtNum } from '../../lib/format'
import { useT } from '../../lib/i18n'
import { useWs } from './useWs'

interface OverviewData {
  kpis: Record<string, string | number>
  series: { day: string; clicks: number; sales: number; revenue: string }[]
  activity: { action: string; actorType: string; at: string }[]
  program: { id: string; name: string; slug: string; currency: string; website: string }
  workspace: { payoutsPaused: boolean; payoutsPausedReason: string | null } | null
  checklist: Record<string, boolean>
  role: string
  linksOrigin: string
  partnersOrigin: string
}

export function OverviewPage() {
  const { t, locale } = useT()
  const ws = useWs()
  const q = useQuery({
    queryKey: ['overview', ws],
    queryFn: () => api<OverviewData>(wsPath(ws, '/overview?days=30')),
  })
  if (q.isLoading || !q.data) return <Spinner />
  const { kpis: k, program, checklist } = q.data
  const cur = program.currency
  const pageUrl = `${location.origin}/join/${program.slug}`
  const steps: [string, boolean, string][] = [
    [t('ckProgram'), checklist.programCreated!, '/settings'],
    [t('ckStripe'), checklist.stripeConnected!, '/settings?tab=integrations'],
    [t('ckPartner'), checklist.firstPartner!, '/partners'],
    [t('ckClick'), checklist.firstClick!, '/partners'],
    [t('ckConversion'), checklist.firstConversion!, '/commissions'],
    [t('ckPayouts'), checklist.payoutsConnected!, '/settings?tab=integrations'],
  ]
  const doneCount = steps.filter((s) => s[1]).length
  return (
    <div>
      <PageHead
        eyebrow={`${program.name} · ${t('last30')}`}
        title={t('overview')}
        actions={
          <>
            <CopyButton value={pageUrl} />
            <a className="btn btn-sm" href={pageUrl} target="_blank" rel="noreferrer">
              {t('programPage')} ↗
            </a>
          </>
        }
      />
      {q.data.workspace?.payoutsPaused && (
        <div role="status" className="card card-pad mb-3 text-sm text-sunset">
          {q.data.workspace.payoutsPausedReason === 'billing_overdue'
            ? t('payoutsPausedBilling')
            : t('payoutsPausedManual')}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          label={t('revenue')}
          value={fmtMoney(k.revenue as string, cur, locale)}
          sub={`${fmtNum(k.sales!, locale)} ${t('sales').toLowerCase()}`}
          tone="ok"
        />
        <Kpi label={t('clicks')} value={fmtNum(k.clicks!, locale)} />
        <Kpi
          label={t('activePartners')}
          value={fmtNum(k.partners!, locale)}
          sub={Number(k.applications) ? `${k.applications} ${t('applications').toLowerCase()}` : undefined}
          tone="warn"
        />
        <Kpi
          label={t('available')}
          value={fmtMoney(k.available as string, cur, locale)}
          sub={`${t('pendingHold')}: ${fmtMoney(k.pending as string, cur, locale)}`}
        />
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-[1.6fr_1fr]">
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <Eyebrow>{t('clicks')} · 30d</Eyebrow>
            <span className="mono text-xs text-mute">
              {t('onHold')}: {fmtMoney(k.on_hold as string, cur, locale)} · {t('paid')}:{' '}
              {fmtMoney(k.paid as string, cur, locale)}
            </span>
          </div>
          <Bars data={q.data.series.map((d) => ({ label: d.day, value: d.clicks }))} />
          <div className="mb-3 mt-6">
            <Eyebrow>{t('sales')} · 30d</Eyebrow>
          </div>
          <Bars data={q.data.series.map((d) => ({ label: d.day, value: d.sales }))} color="#ff7a17" />
        </Card>
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <Eyebrow>{t('setupChecklist')}</Eyebrow>
            <span className="mono text-xs text-mute">
              {doneCount}/{steps.length}
            </span>
          </div>
          <ul className="m-0 list-none p-0">
            {steps.map(([label, ok, to], i) => (
              <li key={label} className="flex items-center gap-3 border-b border-hairline py-3 last:border-0">
                <span
                  className={`mono flex h-6 w-6 items-center justify-center rounded-full border text-[11px] ${ok ? 'border-ok text-ok' : 'border-hairline text-mute'}`}
                >
                  {ok ? '✓' : i + 1}
                </span>
                {ok ? (
                  <span className="text-mute line-through">{label}</span>
                ) : (
                  <Link to={to} className="text-ink no-underline hover:underline">
                    {label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <Simulator currency={cur} />
        <Card>
          <Eyebrow className="mb-4">{t('trackingSnippet')}</Eyebrow>
          <p className="mb-4 text-sm text-body">{t('trackingText')}</p>
          <CodeBlock>{`<script src="${q.data.linksOrigin}/s.js" defer></script>`}</CodeBlock>
          <div className="mt-6">
            <Eyebrow className="mb-3">{t('recentActivity')}</Eyebrow>
            {q.data.activity.length ? (
              <ul className="m-0 list-none p-0">
                {q.data.activity.map((a) => (
                  <li
                    key={`${a.at}-${a.action}`}
                    className="grid grid-cols-[120px_minmax(0,1fr)] gap-3 border-b border-hairline py-2 text-sm last:border-0"
                  >
                    <span className="mono text-xs text-mute">{fmtDate(a.at, locale, true)}</span>
                    <span className="mono truncate text-xs" title={a.action}>
                      {a.action}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-mute">{t('noActivity')}</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}

function Simulator({ currency }: { currency: string }) {
  const { t, locale } = useT()
  const ws = useWs()
  const qc = useQueryClient()
  const partners = useQuery({
    queryKey: ['partners', ws, 'approved'],
    queryFn: () =>
      api<{ partners: { partner_id: string; name: string; email: string }[] }>(
        wsPath(ws, '/partners?status=approved'),
      ),
  })
  const [partnerId, setPartnerId] = useState('')
  const [amount, setAmount] = useState('99')
  const [result, setResult] = useState<string | null>(null)
  const sim = useMutation({
    mutationFn: () =>
      api<{ amount: string | null; commission: { status: string } | null }>(
        wsPath(ws, '/simulate/purchase'),
        {
          body: {
            partnerId: partnerId || partners.data?.partners[0]?.partner_id,
            amountMinor: Math.round(Number(amount) * 100),
            refund: false,
          },
        },
      ),
    onSuccess: (r) => {
      setResult(r.commission ? `${r.amount} · ${r.commission.status}` : '—')
      qc.invalidateQueries()
    },
  })
  const release = useMutation({
    mutationFn: () => api<{ released: number }>(wsPath(ws, '/simulate/release-holds'), { method: 'POST' }),
    onSuccess: (r) => {
      setResult(`+${r.released} → available`)
      qc.invalidateQueries()
    },
  })
  const list = partners.data?.partners ?? []
  return (
    <Card className="glow">
      <div className="mb-2 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-sunset" />
        <Eyebrow>{t('testMode')}</Eyebrow>
      </div>
      <p className="mb-6 text-sm text-body">{t('testModeText')}</p>
      {list.length ? (
        <div className="grid gap-4">
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <Field label={t('partner')}>
              <select className="select" value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
                {list.map((p) => (
                  <option key={p.partner_id} value={p.partner_id}>
                    {p.name} · {p.email}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={`${t('amount')}, ${currency}`}>
              <input
                className="input"
                type="number"
                min={1}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => sim.mutate()}
              disabled={sim.isPending}
            >
              {t('simulatePurchase')}
            </button>
            <button type="button" className="btn btn-sm" onClick={() => release.mutate()}>
              {t('releaseHolds')}
            </button>
            <Link to="/payouts" className="btn btn-sm btn-ghost">
              {t('draftTestBatch')} →
            </Link>
            {result && <span className="mono text-xs text-ok">{result}</span>}
          </div>
          <ErrorNote error={sim.error} />
        </div>
      ) : (
        <p className="text-sm text-mute">
          {t('emptyPartnersText')}{' '}
          <Link to="/partners" className="text-ink">
            {t('invite')} →
          </Link>
        </p>
      )}
      <div className="hidden">{fmtMoney(0, currency, locale)}</div>
    </Card>
  )
}
